import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { LockKey, EnvelopeSimple } from "@phosphor-icons/react";

export default function LoginPage() {
  const { user, login, loading, refresh } = useAuth();
  const [mode, setMode] = useState("password");
  const [email, setEmail] = useState(localStorage.getItem("last_email") || "");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [showActivatePin, setShowActivatePin] = useState(false);
  const nav = useNavigate();

  if (loading) return <div className="h-screen flex items-center justify-center">Memuat...</div>;
  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === "password") {
        await login(email.trim().toLowerCase(), password);
      } else {
        const { data } = await api.post("/auth/login-pin", { email: email.trim().toLowerCase(), pin });
        if (data.access_token) localStorage.setItem("access_token", data.access_token);
        await refresh();
      }
      localStorage.setItem("last_email", email.trim().toLowerCase());
      toast.success("Login berhasil"); nav("/");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Login gagal"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="flex flex-col justify-center px-6 py-10 md:px-16 lg:px-20 max-w-2xl w-full mx-auto lg:mx-0">
        <div className="flex items-center gap-3 mb-10">
          <img src="/logo-gema.png" alt="GEMA" className="w-14 h-14 rounded-xl shadow-lg shadow-indigo-500/20 object-cover"/>
          <div>
            <div className="font-heading text-xl font-bold text-slate-900 leading-tight">GEMA</div>
            <div className="text-xs text-[#4C1D95] tracking-[0.2em] uppercase font-semibold">ERP System</div>
          </div>
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">Masuk ke Akun</h1>
        <p className="text-slate-500 mt-2 mb-6">Kelola produksi, distribusi, keuangan pabrik.</p>
        <div className="flex gap-2 mb-6 bg-slate-100 p-1 rounded-md">
          <button type="button" data-testid="mode-password" onClick={()=>setMode("password")} className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm rounded ${mode==="password"?"bg-white shadow-sm font-semibold":"text-slate-600"}`}><EnvelopeSimple size={16}/> Password</button>
          <button type="button" data-testid="mode-pin" onClick={()=>setMode("pin")} className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm rounded ${mode==="pin"?"bg-white shadow-sm font-semibold":"text-slate-600"}`}><LockKey size={16}/> PIN 6 Digit</button>
        </div>
        <form onSubmit={onSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Email</label>
            <input data-testid="login-email-input" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} className="w-full px-4 py-3 border border-slate-300 rounded-md focus:outline-none focus:border-[#00558F] focus:ring-2 focus:ring-[#00558F]/20 transition-colors" placeholder="nama@email.com"/>
          </div>
          {mode === "password" ? (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-slate-700">Password</label>
                <button type="button" data-testid="forgot-password-link" onClick={()=>setShowForgot(true)} className="text-xs text-[#00558F] hover:text-[#003E6B] font-semibold">Lupa password?</button>
              </div>
              <input data-testid="login-password-input" type="password" required value={password} onChange={(e)=>setPassword(e.target.value)} className="w-full px-4 py-3 border border-slate-300 rounded-md focus:outline-none focus:border-[#00558F] focus:ring-2 focus:ring-[#00558F]/20 transition-colors" placeholder="••••••••"/>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-slate-700">PIN (6 digit)</label>
                <button type="button" data-testid="activate-pin-link" onClick={()=>setShowActivatePin(true)} className="text-xs text-[#00558F] hover:text-[#003E6B] font-semibold">Aktifkan PIN</button>
              </div>
              <input data-testid="login-pin-input" type="password" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required value={pin} onChange={(e)=>setPin(e.target.value.replace(/\D/g,""))} className="w-full px-4 py-3 border border-slate-300 rounded-md text-center font-mono-tab text-2xl tracking-[0.5em] focus:outline-none focus:border-[#00558F] focus:ring-2 focus:ring-[#00558F]/20 transition-colors" placeholder="••••••" autoFocus/>
            </div>
          )}
          <button data-testid="login-submit-btn" type="submit" disabled={submitting} className="w-full py-3 bg-[#00558F] hover:bg-[#003E6B] text-white font-semibold rounded-md transition-colors disabled:opacity-60">{submitting?"Memproses...":"Masuk"}</button>
        </form>
      </div>
      <div className="hidden lg:flex auth-split-image relative">
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-xs tracking-[0.3em] uppercase text-cyan-300 mb-3">GEMA — Kualitas Terjamin</div>
          <h2 className="font-heading text-4xl font-bold leading-tight">Es kristal jernih,<br/>laporan real-time.</h2>
        </div>
      </div>
      {showForgot && <ForgotPasswordModal onClose={()=>setShowForgot(false)}/>}
      {showActivatePin && <ActivatePinModal onClose={()=>setShowActivatePin(false)}/>}
    </div>
  );
}

function ForgotPasswordModal({ onClose }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() }); setSent(true); }
    catch { toast.error("Gagal mengirim email"); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-full max-w-sm" onClick={e=>e.stopPropagation()}>
        <h3 className="font-heading text-lg font-bold text-slate-900 mb-1">Lupa Password</h3>
        {sent ? (
          <>
            <p className="text-sm text-slate-600 my-4">Jika email <strong>{email}</strong> terdaftar, tautan reset password sudah dikirim. Cek inbox / spam Anda dalam beberapa menit.</p>
            <button onClick={onClose} className="w-full py-2 bg-[#00558F] hover:bg-[#003E6B] text-white rounded-md text-sm">Tutup</button>
          </>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-3">
            <p className="text-sm text-slate-500">Masukkan email Anda. Kami kirim tautan reset password ke email tersebut.</p>
            <input data-testid="forgot-email" type="email" required value={email} onChange={e=>setEmail(e.target.value)} className="w-full px-4 py-2 border border-slate-300 rounded-md" placeholder="nama@email.com"/>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="flex-1 py-2 border border-slate-300 rounded-md text-sm">Batal</button>
              <button data-testid="forgot-submit" type="submit" disabled={loading} className="flex-1 py-2 bg-[#00558F] hover:bg-[#003E6B] text-white rounded-md text-sm disabled:opacity-60">{loading?"Mengirim...":"Kirim Tautan Reset"}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function ActivatePinModal({ onClose }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pin.length !== 6) { toast.error("PIN harus 6 digit"); return; }
    setLoading(true);
    try {
      await api.post("/auth/activate-pin", { email: email.trim().toLowerCase(), password, pin });
      toast.success("PIN aktif. Silakan login dengan PIN."); onClose();
    } catch(err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-full max-w-sm" onClick={e=>e.stopPropagation()}>
        <h3 className="font-heading text-lg font-bold text-slate-900 mb-1">Aktifkan PIN</h3>
        <p className="text-sm text-slate-500 mb-4">Masukkan email & password Anda untuk mengaktifkan PIN 6 digit.</p>
        <form onSubmit={submit} className="space-y-3">
          <input type="email" required value={email} onChange={e=>setEmail(e.target.value)} className="w-full px-4 py-2 border border-slate-300 rounded-md text-sm" placeholder="Email"/>
          <input type="password" required value={password} onChange={e=>setPassword(e.target.value)} className="w-full px-4 py-2 border border-slate-300 rounded-md text-sm" placeholder="Password"/>
          <input data-testid="activate-pin-input" type="password" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required value={pin} onChange={e=>setPin(e.target.value.replace(/\D/g,""))} className="w-full px-4 py-2 border border-slate-300 rounded-md text-center text-xl tracking-widest font-mono-tab" placeholder="••••••"/>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2 border border-slate-300 rounded-md text-sm">Batal</button>
            <button data-testid="activate-pin-submit" type="submit" disabled={loading} className="flex-1 py-2 bg-[#00558F] hover:bg-[#003E6B] text-white rounded-md text-sm disabled:opacity-60">{loading?"Menyimpan...":"Aktifkan"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
